import { useCallback, useMemo, useRef } from 'react';
import { Box3, Object3D, Plane, Vector2, Vector2Tuple, Vector3 } from 'three';

import { useDndMonitor } from '@dnd-kit/core';
import {
  AddAction,
  AddActionPayload,
  CommanderActionTypesEnum,
} from '@spazious/dollhouse-commander';
import {
  moveBoxInNavMesh,
  useDollhouse,
  useSceneObjects,
  useSelection,
} from '@spazious/dollhouse-engine';
import { boxFromNode, FNode, FNodeTypesEnum, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { useNavigate } from '@spazious/dollhouse-router';
import { FShallowNode } from '@spazious/dollhouse-shallow-graph';
import { useStore } from '@spazious/dollhouse-store';
import { isTouchEvent, traverseUntil } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

import { ROUTE_PATH_INSPECTOR } from '../constants/routes.js';
import { MainViewMessage } from '../core/EventManager.js';

import { useCommander } from './useCommander.js';
import { useDeviceInfo } from './useDeviceInfo.js';
import { useRestrictedArea } from './useRestrictedArea.js';
export const _target = new Vector3();
export const _vec2 = new Vector2();

const getNormalizedPointer = (elem: HTMLElement, evt: PointerEvent | TouchEvent) => {
  const { clientX, clientY } = isTouchEvent(evt) ? evt.targetTouches[0] : evt;

  return new Vector2().fromArray([
    ((clientX - elem.clientLeft) / elem.clientWidth) * 2.0 - 1.0,
    -((clientY - elem.clientTop) / elem.clientHeight) * 2.0 + 1.0,
  ]);
};

const _box = new Box3();
const _size = new Vector3();
const _center = new Vector3();

export const useDragAndDrop = () => {
  const { select } = useSelection();
  const { isPointInRestrictedArea, confirm } = useRestrictedArea();
  const ignoreRestrictedAreas = useStore(({ settings }) => settings.ignoreRestrictedAreas);

  const set = useStore(({ set }) => set);
  const pointer = null; //useDollhouse(({ pointer }) => pointer);
  const scene = useDollhouse(({ scene }) => scene);
  const raycaster = useDollhouse(({ raycaster }) => raycaster);
  const camera = useDollhouse(({ camera }) => camera);
  const dollhouseEvents = useDollhouse(({ events }) => events);
  const gl = useDollhouse(({ gl }) => gl);
  const domElement = gl?.domElement ?? dollhouseEvents.connected;
  const navMeshQuery = useDollhouse(({ world }) => world?.navMeshQuery);

  const plane = useMemo(() => new Plane(camera.up, 0), [camera.up]);

  const isMobile = useDeviceInfo((state) => state.isMobile);
  const eventType = isMobile ? 'touchmove' : 'pointermove';

  const pendingSelector = useRef<string | null>(null);

  const boxRef = useRef<Box3 | null>(null);
  const navigate = useNavigate();

  const commander = useCommander();
  const actionRef = useRef<AddAction | null>(null);
  const payloadRef = useRef<AddActionPayload | null>(null);
  const positionRef = useRef<Vector2Tuple | null>(null);

  const [sceneObjects] = useSceneObjects(scene, (obj) => !obj.userData.controls);

  const objectsToBeSelected = useMemo(
    () =>
      sceneObjects.filter(
        (obj) =>
          !obj.userData.controls && pendingSelector.current === getNodeFromObject(obj)?.selector
      ),
    [sceneObjects]
  );

  const openInspectorPanel = useEventCallback((found: Object3D) => {
    const { userData } = found;
    if (userData.node?.type === FNodeTypesEnum.FComposition) {
      navigate(ROUTE_PATH_INSPECTOR);
    }
  });

  const getNodeBox = useCallback(() => {
    if (!boxRef.current && pendingSelector.current) {
      let node: FNode | null = null;
      traverseUntil<Object3D>(scene, (obj) => {
        const cNode = getNodeFromObject(obj);
        if (cNode?.selector === pendingSelector.current) {
          node = cNode as FNode;
          return false;
        }
      });

      if (node && boxFromNode(node, _box).success) {
        boxRef.current = _box;
      }
    }

    return boxRef.current;
  }, [scene]);

  const handlePointerMove = useEventCallback((evt: PointerEvent | TouchEvent) => {
    if (pendingSelector.current) {
      const ptr = pointer ?? getNormalizedPointer(domElement, evt);
      raycaster.setFromCamera(ptr, camera);
      raycaster.ray.intersectPlane(plane, _target);

      const box = getNodeBox();
      let position: Vector2Tuple | null = null;
      if (box && !box.isEmpty() && navMeshQuery) {
        const center = box.getCenter(_center);
        const size = box.getSize(_size);

        const { movement } = moveBoxInNavMesh(
          [center.x, center.z],
          [size.x, size.z],
          0,
          _target.sub(center),
          navMeshQuery
        );
        movement.y = 0;
        _center.add(movement);
        position = [_center.x, _center.z];
        box.setFromCenterAndSize(_center, size);
      } else {
        const result = navMeshQuery?.findClosestPoint(_target);
        if (result?.success) {
          position = [result.point.x, result.point.z];
        }
      }

      if (position) {
        positionRef.current = [...position];
        payloadRef.current = { ...payloadRef.current!, position } as FShallowNode;
        actionRef.current?.update(payloadRef.current);
      }
    }
  });

  const abort = useEventCallback(() => {
    document.removeEventListener(eventType, handlePointerMove);
    actionRef.current?.abort();
    pendingSelector.current = null;
    actionRef.current = null;
  });

  const commit = useEventCallback(() => {
    document.removeEventListener(eventType, handlePointerMove);

    // TODO: This should be handled by the commander, which should handle the selection
    if (objectsToBeSelected.length && pendingSelector.current) {
      select(objectsToBeSelected);
      if (objectsToBeSelected.length === 1) {
        openInspectorPanel(objectsToBeSelected[0]);
      }
    }
    pendingSelector.current = null;

    actionRef.current?.commit();
    actionRef.current = null;
  });

  useDndMonitor({
    onDragOver(evt) {
      const { node } = evt.active.data.current as {
        node: FShallowNode;
      };
      payloadRef.current = { ...node };
      actionRef.current = commander.createAction(CommanderActionTypesEnum.Add, payloadRef.current);

      pendingSelector.current = actionRef.current.selector();
      set({
        showNavMesh: true,
      });
      handlePointerMove(evt.activatorEvent as PointerEvent | TouchEvent);
      document.addEventListener(eventType, handlePointerMove);
    },
    onDragEnd() {
      if (!actionRef.current) {
        return;
      }

      boxRef.current = null;
      set({
        showNavMesh: false,
      });

      if (!ignoreRestrictedAreas && isPointInRestrictedArea(positionRef.current!)) {
        confirm((message: MainViewMessage) => {
          if (message === MainViewMessage.RestrictedAreaActionRevert) {
            abort();
          } else if (message === MainViewMessage.RestrictedAreaActionIgnore) {
            commit();
          }
        });
      } else {
        commit();
      }
    },
    onDragCancel() {
      boxRef.current = null;
      set({
        showNavMesh: false,
      });
      abort();
    },
  });

  return { box: boxRef.current };
};
